import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { fetchPartnerStatusCandidates, type PartnerStatusCandidate } from '@/services/partnerStatusService'

// `display_name` — ФОРМА имени в списках/рейтингах/ШМП. Юридическое ФИО сюда не
// входит: оно меняется юристами на карточке ДКК (LegalNameChangeModal),
// см. docs/PARTY_IDENTITY_CHANGE_PLAN.md.
export type PartnerGroupMembershipMode = 'add_person' | 'end_person' | 'display_name'

export type PartnerGroupMembershipRow = {
  person_id: number
  first_name: string
  last_name: string
  partner_number: number
  start_date: string | null
  end_date: string | null
  membership_status: 'АКТИВЕН' | 'НЕАКТИВЕН'
}

export type PartnerPersonExistsRow = {
  person_id: number
  first_name: string
  last_name: string
  partner_number: number | null
  partner_exists: boolean
}

export type AddPersonToPartnerGroupResult = {
  partner_number: number
  person_id: number
  first_name: string
  last_name: string
  gender: 'м' | 'ж'
  tg_username: string | null
  start_date: string
}

export type EndPartnerGroupMembershipResult = {
  partner_number: number
  person_id: number | null
  first_name: string
  last_name: string
  end_date: string
  updated_count: number
}

export type ChangePartnerPersonLastNameResult = {
  partner_number: number
  person_id: number
  first_name: string
  old_last_name: string
  new_last_name: string
  updated_count: number
}

export async function fetchPartnerGroupCandidates(): Promise<PartnerStatusCandidate[]> {
  return fetchPartnerStatusCandidates()
}

export async function checkPartnerPersonExists(params: {
  firstName: string
  lastName: string
}): Promise<PartnerPersonExistsRow[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('check_partner_person_exists', {
      p_first_name: params.firstName.trim(),
      p_last_name: params.lastName.trim(),
    })
  )) as { data: PartnerPersonExistsRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchPartnerGroupMembership(partnerNumber: number): Promise<PartnerGroupMembershipRow[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_partner_group_membership', {
      p_partner_number: partnerNumber,
    })
  )) as { data: PartnerGroupMembershipRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function addPersonToPartnerGroup(params: {
  partnerNumber: number
  firstName: string
  lastName: string
  gender: 'м' | 'ж'
  tgUsername?: string | null
  startDate: string
}): Promise<AddPersonToPartnerGroupResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('add_person_to_partner_group', {
      v_partner_number: params.partnerNumber,
      v_first_name: params.firstName.trim(),
      v_last_name: params.lastName.trim(),
      v_gender: params.gender,
      v_tg_username: params.tgUsername?.trim() || null,
      v_start_date: params.startDate,
    })
  )) as { data: AddPersonToPartnerGroupResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Персона не добавлена')
  return data
}

export async function endPartnerGroupMembership(params: {
  partnerNumber: number
  firstName: string
  lastName: string
  endDate: string
}): Promise<EndPartnerGroupMembershipResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('end_partner_group_membership', {
      v_first_name: params.firstName.trim(),
      v_last_name: params.lastName.trim(),
      v_partner_number: params.partnerNumber,
      v_end_date: params.endDate,
    })
  )) as { data: EndPartnerGroupMembershipResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Участие персоны не закрыто')
  return data
}

/**
 * @deprecated 19.08.2026: юридическая смена ФИО переехала к юристам в реестр
 * документов (`applyContractNameChange`). Из UI не вызывается. RPC под ней осталась
 * рабочей обёрткой, которая проводит смену через журнал с пометкой LEGACY_UI —
 * оставить её работающей мимо журнала было бы хуже, чем провести через журнал.
 */
export async function changePartnerPersonLastName(params: {
  partnerNumber: number
  oldFirstName: string
  oldLastName: string
  newLastName: string
}): Promise<ChangePartnerPersonLastNameResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('change_partner_person_last_name_from_original_case', {
      _partner_number: String(params.partnerNumber),
      _old_first_name: params.oldFirstName.trim(),
      _old_last_name: params.oldLastName.trim(),
      _new_last_name: params.newLastName.trim(),
    })
  )) as { data: ChangePartnerPersonLastNameResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Фамилия не изменена')
  return data
}
